import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { testResultAPI, testCaseAPI } from '../services/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function ResultDetail() {
  const { id } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailedResults, setDetailedResults] = useState(null);
  const [loadingS3Results, setLoadingS3Results] = useState(false);
  const [s3Error, setS3Error] = useState(null);
  const [testCase, setTestCase] = useState(null);

  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [pagination, setPagination] = useState(null);
  const [filtering, setFiltering] = useState(null);
  const [filters, setFilters] = useState({
    minAttempts: '',
    maxAttempts: '',
    statusCode: '',
    success: null,
    hasError: null
  });

  // Stage and traffic management
  const [updatingStage, setUpdatingStage] = useState(false);
  const [manualTraffic, setManualTraffic] = useState('');
  const [savingTraffic, setSavingTraffic] = useState(false);

  // Clipboard helper with fallback
  const copyToClipboard = async (text, successMessage = 'Copied to clipboard!') => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for browsers without clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      alert(successMessage);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      // Show the text in a prompt as last resort
      prompt('Copy this text:', text);
    }
  };

  useEffect(() => {
    fetchResult();
  }, [id]);

  const fetchResult = async () => {
    try {
      setLoading(true);
      const response = await testResultAPI.getById(id);
      setResult(response.data);
      setManualTraffic(response.data.trafficData?.manualTraffic || '');

      // Check if results are stored in S3 or inline
      if (response.data.s3ResultsUrl) {
        // Results are in S3, will load on demand
        setDetailedResults(null);
      } else if (response.data.detailedResults && response.data.detailedResults.length > 0) {
        // Results are inline
        setDetailedResults(response.data.detailedResults);
      }

      // Always fetch test case to get auth and other config for curl generation
      if (response.data.testCaseId) {
        try {
          const testCaseResponse = await testCaseAPI.getById(response.data.testCaseId);
          setTestCase(testCaseResponse.data);
        } catch (err) {
          console.error('Failed to fetch test case:', err);
        }
      }

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchS3Results = async (page = 1, appliedFilters = filters) => {
    if (!result?.s3ResultsUrl) {
      return; // Not in S3
    }

    try {
      setLoadingS3Results(true);
      setS3Error(null);

      // Build query params
      const params = {
        page,
        limit: itemsPerPage,
      };

      // Add filters if they have values
      if (appliedFilters.minAttempts) params.minAttempts = appliedFilters.minAttempts;
      if (appliedFilters.maxAttempts) params.maxAttempts = appliedFilters.maxAttempts;
      if (appliedFilters.statusCode) params.statusCode = appliedFilters.statusCode;
      if (appliedFilters.success !== null) params.success = appliedFilters.success;
      if (appliedFilters.hasError !== null) params.hasError = appliedFilters.hasError;

      const response = await testResultAPI.getS3Results(id, params);
      setDetailedResults(response.data.detailedResults);
      setPagination(response.data.pagination);
      setFiltering(response.data.filtering);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching S3 results:', err);
      setS3Error(err.message || 'Failed to load detailed results from S3');
    } finally {
      setLoadingS3Results(false);
    }
  };

  const downloadS3Results = async () => {
    try {
      const response = await testResultAPI.getS3DownloadUrl(id);

      // Fetch the file content from S3
      const fileResponse = await fetch(response.data.downloadUrl);
      const blob = await fileResponse.blob();

      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading S3 results:', err);
      alert('Failed to download S3 results: ' + err.message);
    }
  };

  const handleStageUpdate = async (newStage) => {
    try {
      setUpdatingStage(true);
      await testResultAPI.updateStage(id, newStage);
      await fetchResult();
    } catch (err) {
      alert('Failed to update stage: ' + err.message);
    } finally {
      setUpdatingStage(false);
    }
  };

  const handleTrafficSave = async () => {
    try {
      setSavingTraffic(true);
      const traffic = parseFloat(manualTraffic) || null;
      await testResultAPI.updateTraffic(id, traffic);
      await fetchResult();
      alert('Traffic updated successfully');
    } catch (err) {
      alert('Failed to update traffic: ' + err.message);
    } finally {
      setSavingTraffic(false);
    }
  };

  const exportAsJSON = () => {
    const dataStr = JSON.stringify(result, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `test-result-${result.testCaseName}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportAsCSV = async () => {
    // Ensure detailed results are loaded
    if (!detailedResults) {
      if (result?.s3ResultsUrl) {
        await fetchS3Results();
        if (!detailedResults) {
          alert('Failed to load detailed results');
          return;
        }
      } else {
        alert('No detailed results available to export');
        return;
      }
    }

    const headers = ['Test ID', 'Status Code', 'Latency (ms)', 'Request Size (bytes)', 'Response Size (bytes)', 'Success', 'Error', 'Timestamp'];
    const rows = detailedResults.map(r => [
      r.test_id || '',
      r.status_code || '',
      r.latency_ms || '',
      r.request_size_bytes || '',
      r.response_size_bytes || '',
      r.success ? 'Yes' : 'No',
      r.error || '',
      r.timestamp || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `test-result-${result.testCaseName}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportAsMarkdown = () => {
    const metrics = result.metrics || {};
    const latency = metrics.latency_metrics || {};
    const config = result.executionConfig || {};

    const markdown = `## Endpoint: ${result.testCaseName}
**Concurrency:** ${config.concurrency || 'N/A'}
**Total requests:** ${metrics.total_requests || 0}
✅ **Success:** ${metrics.successful_requests || 0}
❌ **Failed:** ${metrics.failed_requests || 0}
⏱️ **Avg Latency:** ${latency.avg?.toFixed(2) || 0}s
📊 **P50:** ${(latency.p50 / 1000)?.toFixed(2) || 0}s | **P90:** ${(latency.p95 / 1000)?.toFixed(2) || 0}s | **P95:** ${(latency.p95 / 1000)?.toFixed(2) || 0}s | **P99:** ${(latency.p99 / 1000)?.toFixed(2) || 0}s

**Execution Time:** ${metrics.execution_time_ms ? (metrics.execution_time_ms / 1000).toFixed(2) : 0}s
**Success Rate:** ${metrics.success_rate?.toFixed(2) || 0}%
**Date:** ${new Date(result.createdAt).toLocaleString()}`;

    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `test-result-${result.testCaseName}-${new Date().toISOString().split('T')[0]}.md`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyMarkdownToClipboard = async () => {
    const metrics = result.metrics || {};
    const latency = metrics.latency_metrics || {};
    const config = result.executionConfig || {};

    const markdown = `Endpoint: ${result.testCaseName}
Concurrency: ${config.concurrency || 'N/A'}
Total requests: ${metrics.total_requests || 0}
:white_check_mark: Success: ${metrics.successful_requests || 0}
:x: Failed: ${metrics.failed_requests || 0}
:stopwatch: Avg Latency: ${(latency.avg / 1000)?.toFixed(2) || 0}s
:bar_chart: P50: ${(latency.p50 / 1000)?.toFixed(2) || 0}s | P90: ${(latency.p95 / 1000)?.toFixed(2) || 0}s | P95: ${(latency.p95 / 1000)?.toFixed(2) || 0}s | P99: ${(latency.p99 / 1000)?.toFixed(2) || 0}s`;

    await copyToClipboard(markdown, 'Copied to clipboard! Ready to paste in Slack.');
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="card">
        <div className="alert alert-danger">
          {error || 'Result not found'}
        </div>
        <Link to="/results" className="btn btn-secondary">Back to Results</Link>
      </div>
    );
  }

  const metrics = result.metrics || {};
  const latency = metrics.latency_metrics || {};
  const requestSize = metrics.request_size_metrics || {};
  const responseSize = metrics.response_size_metrics || {};

  // Prepare data for charts
  const latencyData = [
    { name: 'Min', value: latency.min || 0 },
    { name: 'P50', value: latency.p50 || 0 },
    { name: 'P95', value: latency.p95 || 0 },
    { name: 'P99', value: latency.p99 || 0 },
    { name: 'Max', value: latency.max || 0 },
  ];

  const statusData = Object.entries(metrics.status_distribution || {}).map(([code, count]) => ({
    name: `HTTP ${code}`,
    value: count
  }));

  const COLORS = ['#27ae60', '#3498db', '#f39c12', '#e74c3c', '#9b59b6'];

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <h2>Test Result: {result.testCaseName}</h2>
          <div className="flex">
            <button onClick={copyMarkdownToClipboard} className="btn btn-success" title="Copy Slack-formatted summary">
              Copy for Slack
            </button>
            <button onClick={exportAsMarkdown} className="btn btn-primary" title="Download as Markdown">
              Export MD
            </button>
            <button onClick={exportAsJSON} className="btn btn-primary" title="Download full result as JSON">
              Export JSON
            </button>
            <button onClick={exportAsCSV} className="btn btn-primary" title="Download detailed results as CSV">
              Export CSV
            </button>
            <Link to={`/test-case/${result.testCaseId}`} className="btn btn-secondary">
              View Test Case
            </Link>
            <Link to="/results" className="btn btn-secondary">
              Back to Results
            </Link>
          </div>
        </div>

        {/* Stage Management */}
        <div className="card mb-3" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
          <div className="flex-between">
            <div>
              <h4 style={{ margin: '0 0 0.5rem 0' }}>Stage</h4>
              <div>
                <span className={`badge badge-${result.stage || 'draft'}`} style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                  {result.stage || 'draft'}
                </span>
                {result.isPerformance && (
                  <span className="badge badge-performance ml-2" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                    ⭐ Performance
                  </span>
                )}
              </div>
            </div>
            <div className="btn-group">
              {result.stage === 'draft' && (
                <button
                  onClick={() => handleStageUpdate('main')}
                  disabled={updatingStage}
                  className="btn btn-primary"
                >
                  {updatingStage ? 'Updating...' : 'Promote to Main'}
                </button>
              )}
              {result.stage === 'main' && (
                <>
                  <button
                    onClick={() => handleStageUpdate('draft')}
                    disabled={updatingStage}
                    className="btn btn-secondary"
                  >
                    {updatingStage ? 'Updating...' : 'Demote to Draft'}
                  </button>
                  {!result.isPerformance ? (
                    <button
                      onClick={() => handleStageUpdate('performance')}
                      disabled={updatingStage}
                      className="btn btn-success"
                    >
                      {updatingStage ? 'Updating...' : '⭐ Star for Performance'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStageUpdate('main')}
                      disabled={updatingStage}
                      className="btn btn-warning"
                    >
                      {updatingStage ? 'Updating...' : 'Unstar from Performance'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Traffic Data */}
        <div className="card mb-3" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>Traffic Data</h4>
          <div className="grid grid-2">
            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                Auto-Calculated Traffic (from Proxy API)
              </label>
              <p style={{ margin: 0 }}>
                {result.trafficData?.autoCalculatedTraffic ? (
                  <>
                    <strong>{result.trafficData.autoCalculatedTraffic.toFixed(2)} MB</strong>
                    {result.trafficData.trafficCalculatedAt && (
                      <span className="text-muted" style={{ marginLeft: '0.5rem', fontSize: '0.85em' }}>
                        (calculated {new Date(result.trafficData.trafficCalculatedAt).toLocaleString()})
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-muted">Not available</span>
                )}
              </p>
            </div>
            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                Manual Traffic (MB)
              </label>
              <div className="flex">
                <input
                  type="number"
                  step="0.01"
                  value={manualTraffic}
                  onChange={(e) => setManualTraffic(e.target.value)}
                  placeholder="Enter traffic in MB"
                  style={{ flex: 1 }}
                />
                <button
                  onClick={handleTrafficSave}
                  disabled={savingTraffic}
                  className="btn btn-primary"
                >
                  {savingTraffic ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
          {(result.trafficData?.autoCalculatedTraffic || result.trafficData?.manualTraffic) && (
            <div className="mt-2">
              <strong>Total Traffic: </strong>
              {((result.trafficData?.autoCalculatedTraffic || 0) + (result.trafficData?.manualTraffic || 0)).toFixed(2)} MB
            </div>
          )}
        </div>

        {/* Key Metrics */}
        <div className="metrics-grid">
          <div className="metric-card success">
            <div className="metric-label">Total Requests</div>
            <div className="metric-value">{metrics.total_requests || 0}</div>
          </div>

          <div className="metric-card success">
            <div className="metric-label">Successful</div>
            <div className="metric-value">{metrics.successful_requests || 0}</div>
          </div>

          <div className="metric-card danger">
            <div className="metric-label">Failed</div>
            <div className="metric-value">{metrics.failed_requests || 0}</div>
          </div>

          <div className={`metric-card ${metrics.success_rate >= 95 ? 'success' : 'warning'}`}>
            <div className="metric-label">Success Rate</div>
            <div className="metric-value">{metrics.success_rate || 0}%</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Avg Latency</div>
            <div className="metric-value">{latency.avg || 0}ms</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Execution Time</div>
            <div className="metric-value">{(metrics.execution_time_ms / 1000).toFixed(2)}s</div>
          </div>
        </div>

        {/* Execution Configuration */}
        <div className="card" style={{ background: '#f8f9fa' }}>
          <h3>Execution Configuration</h3>
          <div className="grid grid-3">
            <p><strong>Range:</strong> {result.executionConfig?.startRange} - {result.executionConfig?.endRange}</p>
            <p><strong>Concurrency:</strong> {result.executionConfig?.concurrency}</p>
            <p><strong>Detailed Outputs:</strong> {result.executionConfig?.produce_outputs ? 'Yes' : 'No'}</p>
          </div>
          {result.proxyName && (
            <div style={{ marginTop: '1rem' }}>
              <p><strong>Proxy Used:</strong> {result.proxyName}</p>
            </div>
          )}
          {result.proxyListName && (
            <div style={{ marginTop: '1rem' }}>
              <p><strong>Proxy List Used:</strong> {result.proxyListName}</p>
              {result.executionConfig?.proxy_strategy && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  <p><strong>Strategy:</strong></p>
                  <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                    <li>Max Retries: {result.executionConfig.proxy_strategy.max_retries}</li>
                    <li>Max Requests per Proxy: {result.executionConfig.proxy_strategy.max_requests_per_proxy || 'Unlimited'}</li>
                    <li>Remove on Failure: {result.executionConfig.proxy_strategy.remove_on_failure ? 'Yes' : 'No'}</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Charts */}
        <div className="grid grid-2 mt-3">
          <div className="card">
            <h3>Latency Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={latencyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#3498db" name="Latency (ms)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <h3>Status Code Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Retry Distribution Chart */}
          {metrics.retry_distribution && (
            <div className="card">
              <h3>Retry Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={Object.entries(metrics.retry_distribution)
                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                  .map(([attempt, count]) => ({
                    name: `Attempt ${attempt}`,
                    value: count
                  }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#9b59b6" name="Successful Requests" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Detailed Metrics Tables */}
        <div className="grid grid-3 mt-3">
          <div className="card">
            <h3>Latency Metrics (ms)</h3>
            <table>
              <tbody>
                <tr><td>Min</td><td>{latency.min || 0}</td></tr>
                <tr><td>Average</td><td>{latency.avg || 0}</td></tr>
                <tr><td>P50</td><td>{latency.p50 || 0}</td></tr>
                <tr><td>P95</td><td>{latency.p95 || 0}</td></tr>
                <tr><td>P99</td><td>{latency.p99 || 0}</td></tr>
                <tr><td>Max</td><td>{latency.max || 0}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="card">
            <h3>Request Size (bytes)</h3>
            <table>
              <tbody>
                <tr><td>Min</td><td>{requestSize.min || 0}</td></tr>
                <tr><td>Average</td><td>{requestSize.avg || 0}</td></tr>
                <tr><td>P50</td><td>{requestSize.p50 || 0}</td></tr>
                <tr><td>P95</td><td>{requestSize.p95 || 0}</td></tr>
                <tr><td>P99</td><td>{requestSize.p99 || 0}</td></tr>
                <tr><td>Max</td><td>{requestSize.max || 0}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="card">
            <h3>Response Size (bytes)</h3>
            <table>
              <tbody>
                <tr><td>Min</td><td>{responseSize.min || 0}</td></tr>
                <tr><td>Average</td><td>{responseSize.avg || 0}</td></tr>
                <tr><td>P50</td><td>{responseSize.p50 || 0}</td></tr>
                <tr><td>P95</td><td>{responseSize.p95 || 0}</td></tr>
                <tr><td>P99</td><td>{responseSize.p99 || 0}</td></tr>
                <tr><td>Max</td><td>{responseSize.max || 0}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Retry Distribution Table */}
        {metrics.retry_distribution && (
          <div className="card mt-3">
            <h3>Retry Distribution</h3>
            <table>
              <thead>
                <tr>
                  <th>Attempt</th>
                  <th>Count</th>
                  <th>Percentage</th>
                  <th>Cumulative</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(metrics.retry_distribution)
                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                  .map(([attempt, count], index, arr) => {
                    const percentage = ((count / metrics.total_requests) * 100).toFixed(2);
                    const cumulative = arr.slice(0, index + 1).reduce((sum, [, c]) => sum + c, 0);
                    return (
                      <tr key={attempt}>
                        <td>Attempt {attempt}</td>
                        <td>{count}</td>
                        <td>{percentage}%</td>
                        <td>{cumulative} ({((cumulative / metrics.total_requests) * 100).toFixed(2)}%)</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {/* Error Types */}
        {metrics.error_types && Object.keys(metrics.error_types).length > 0 && (
          <div className="card mt-3">
            <h3>Error Types</h3>
            <table>
              <thead>
                <tr>
                  <th>Error Type</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(metrics.error_types).map(([error, count]) => (
                  <tr key={error}>
                    <td>{error}</td>
                    <td>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detailed Results */}
        {(result.s3ResultsUrl || (result.detailedResults && result.detailedResults.length > 0)) && (
          <div className="card mt-3">
            {result.s3ResultsUrl && !detailedResults ? (
              <div>
                <h3>Detailed Results ({result.detailedResultsCount || 'Unknown'} records - Stored in S3)</h3>
                {s3Error && (
                  <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                    {s3Error}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button
                    onClick={() => fetchS3Results(1)}
                    disabled={loadingS3Results}
                    className="btn btn-primary"
                  >
                    {loadingS3Results ? 'Loading from S3...' : 'Load Detailed Results from S3'}
                  </button>
                  <button
                    onClick={downloadS3Results}
                    className="btn btn-success"
                  >
                    ⬇ Download Full JSON
                  </button>
                </div>
                <p className="text-muted">
                  Results are stored in S3 to reduce MongoDB size. Click "Load" to view them or "Download" to get the full JSON file.
                </p>
              </div>
            ) : detailedResults && detailedResults.length > 0 ? (
              <>
                {/* Filter Controls */}
                {result.s3ResultsUrl && (
                  <div className="card mb-3" style={{ background: '#f8f9fa', padding: '1rem' }}>
                    <h4 style={{ marginBottom: '1rem' }}>Filters</h4>
                    <div className="grid grid-3" style={{ marginBottom: '1rem' }}>
                      {/* Attempts Filter */}
                      {detailedResults.some(d => d.proxy_attempts && d.proxy_attempts.length > 0) && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Proxy Attempts
                          </label>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                              type="number"
                              placeholder="Min"
                              value={filters.minAttempts}
                              onChange={(e) => setFilters({...filters, minAttempts: e.target.value})}
                              style={{ flex: 1 }}
                              min="1"
                            />
                            <input
                              type="number"
                              placeholder="Max"
                              value={filters.maxAttempts}
                              onChange={(e) => setFilters({...filters, maxAttempts: e.target.value})}
                              style={{ flex: 1 }}
                              min="1"
                            />
                          </div>
                        </div>
                      )}

                      {/* Status Code Filter */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                          Status Code
                        </label>
                        <select
                          value={filters.statusCode}
                          onChange={(e) => setFilters({...filters, statusCode: e.target.value})}
                          style={{ width: '100%' }}
                        >
                          <option value="">All Status Codes</option>
                          <option value="200">200 - OK</option>
                          <option value="201">201 - Created</option>
                          <option value="400">400 - Bad Request</option>
                          <option value="401">401 - Unauthorized</option>
                          <option value="403">403 - Forbidden</option>
                          <option value="404">404 - Not Found</option>
                          <option value="500">500 - Server Error</option>
                          <option value="502">502 - Bad Gateway</option>
                          <option value="503">503 - Service Unavailable</option>
                        </select>
                      </div>

                      {/* Success/Error Filter */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                          Result Status
                        </label>
                        <select
                          value={filters.success === null ? '' : filters.success.toString()}
                          onChange={(e) => setFilters({...filters, success: e.target.value === '' ? null : e.target.value === 'true'})}
                          style={{ width: '100%' }}
                        >
                          <option value="">All Results</option>
                          <option value="true">✓ Success Only</option>
                          <option value="false">✗ Failed Only</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <button
                        onClick={() => fetchS3Results(1, filters)}
                        className="btn btn-primary"
                        disabled={loadingS3Results}
                      >
                        {loadingS3Results ? 'Applying...' : 'Apply Filters'}
                      </button>
                      <button
                        onClick={() => {
                          setFilters({
                            minAttempts: '',
                            maxAttempts: '',
                            statusCode: '',
                            success: null,
                            hasError: null
                          });
                          fetchS3Results(1, {
                            minAttempts: '',
                            maxAttempts: '',
                            statusCode: '',
                            success: null,
                            hasError: null
                          });
                        }}
                        className="btn btn-secondary"
                        disabled={loadingS3Results}
                      >
                        Clear Filters
                      </button>

                      {/* Show filter summary */}
                      {filtering && (
                        <div style={{ marginLeft: 'auto', color: '#666', fontSize: '0.9rem' }}>
                          Showing <strong>{filtering.filteredItems}</strong> of <strong>{filtering.totalItems}</strong> results
                          {filtering.filteredItems < filtering.totalItems && (
                            <span> (filtered)</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <h3>Detailed Results ({detailedResults.length} records{result.s3ResultsUrl ? ' - Loaded from S3' : ''})</h3>
                <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>View</th>
                        <th>Test ID</th>
                        <th>Status</th>
                        <th>Latency</th>
                        <th>Request Size</th>
                        <th>Response Size</th>
                        <th>Success</th>
                        {(result.proxyListName || result.proxyName || detailedResults.some(d => d.proxy_attempts && d.proxy_attempts.length > 0)) && <th>Proxy</th>}
                        {detailedResults.some(d => d.proxy_attempts && d.proxy_attempts.length > 0) && <th>Attempts</th>}
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailedResults.map((detail, index) => (
                    <React.Fragment key={index}>
                      <tr style={{ cursor: (detail.response_body || detail.input) ? 'pointer' : 'default' }}>
                        <td>
                          {(detail.response_body || detail.input) && (
                            <button
                              onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                              className="btn btn-primary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            >
                              {expandedRow === index ? '▼' : '▶'}
                            </button>
                          )}
                        </td>
                        <td>{detail.test_id}</td>
                        <td>
                          <span className={`badge ${detail.status_code >= 200 && detail.status_code < 300 ? 'badge-success' : 'badge-danger'}`}>
                            {detail.status_code}
                          </span>
                        </td>
                        <td>{detail.latency_ms}ms</td>
                        <td>{detail.request_size_bytes}B</td>
                        <td>{detail.response_size_bytes}B</td>
                        <td>
                          <span className={detail.success ? 'badge badge-success' : 'badge badge-danger'}>
                            {detail.success ? '✓' : '✗'}
                          </span>
                        </td>
                        {(result.proxyListName || result.proxyName || detailedResults.some(d => d.proxy_attempts && d.proxy_attempts.length > 0)) && (
                          <td>
                            {detail.final_proxy_used || detail.proxy_attempts?.[detail.proxy_attempts.length - 1]?.proxy_url ? (
                              <code style={{ fontSize: '0.75rem', display: 'block', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {detail.proxy_attempts?.[detail.proxy_attempts.length - 1]?.proxy_url || detail.proxy_attempts?.[detail.proxy_attempts.length - 1]?.proxy_name || detail.final_proxy_used || 'No Proxy'}
                              </code>
                            ) : '-'}
                          </td>
                        )}
                        {detailedResults.some(d => d.proxy_attempts && d.proxy_attempts.length > 0) && (
                          <td>
                            {detail.proxy_attempts && detail.proxy_attempts.length > 0 ? (
                              <span className={`badge ${detail.proxy_attempts.length > 1 ? 'badge-warning' : 'badge-success'}`}>
                                {detail.proxy_attempts.length}
                              </span>
                            ) : '-'}
                          </td>
                        )}
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {detail.error || '-'}
                        </td>
                      </tr>
                      {expandedRow === index && (
                        <tr>
                          <td colSpan={result.proxyListName ? "10" : "8"} style={{ background: '#f9f9f9', padding: '1rem' }}>
                            {/* Proxy Attempts Section */}
                            {detail.proxy_attempts && detail.proxy_attempts.length > 0 && (
                              <div style={{ marginBottom: '1.5rem' }}>
                                <strong style={{ fontSize: '1rem' }}>Proxy Attempts ({detail.proxy_attempts.length}):</strong>
                                <div style={{ marginTop: '0.5rem' }}>
                                  {detail.proxy_attempts.map((attempt, attemptIdx) => (
                                    <div
                                      key={attemptIdx}
                                      style={{
                                        background: attempt.success ? '#d4edda' : '#f8d7da',
                                        border: `1px solid ${attempt.success ? '#c3e6cb' : '#f5c6cb'}`,
                                        borderRadius: '4px',
                                        padding: '0.75rem',
                                        marginBottom: '0.5rem'
                                      }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                        <span>
                                          <strong>Attempt #{attempt.attempt_number}</strong>
                                        </span>
                                        <span className={`badge ${attempt.success ? 'badge-success' : 'badge-danger'}`}>
                                          {attempt.status_code} {attempt.success ? '✓' : '✗'}
                                        </span>
                                      </div>
                                      <div style={{ fontSize: '0.85rem', color: '#555' }}>
                                        <div><strong>Proxy:</strong> <code style={{ fontSize: '0.8rem' }}>{attempt.proxy_url || attempt.proxy_name}</code></div>
                                        <div>Latency: {attempt.latency_ms}ms</div>
                                        {attempt.error && (
                                          <div style={{
                                            color: '#721c24',
                                            background: '#f8d7da',
                                            padding: '0.5rem',
                                            borderRadius: '4px',
                                            marginTop: '0.5rem',
                                            fontWeight: 'bold'
                                          }}>
                                            ⚠️ Error: {attempt.error}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Input Data Section */}
                            {detail.input && (
                              <>
                                <div style={{ marginBottom: '1rem' }}>
                                  <strong style={{ fontSize: '1rem' }}>Input Data:</strong>
                                  <button
                                    onClick={() => copyToClipboard(JSON.stringify(detail.input, null, 2), 'Input data copied to clipboard!')}
                                    className="btn btn-secondary"
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', marginLeft: '1rem' }}
                                  >
                                    Copy JSON
                                  </button>
                                  <button
                                    onClick={async () => {
                                      try {
                                        // Generate curl command - use endpoint from result, fallback to testCase
                                        const baseEndpoint = result.endpoint || testCase?.endpoint || '';
                                        const method = result.method || testCase?.method || 'GET';

                                        if (!baseEndpoint) {
                                          alert('Endpoint URL not available. Unable to generate cURL command.');
                                          return;
                                        }

                                        let url = `${baseEndpoint}${detail.input.path || ''}`;

                                        // Build query params - start with ALL input query params (already includes proxy if it was passed)
                                        const allQueryParams = { ...(detail.input.query_params || {}) };

                                        // Get the actual proxy URL used (not just username)
                                        const proxyUrl = detail.final_proxy_used || detail.proxy_attempts?.[detail.proxy_attempts.length - 1]?.proxy_url;

                                        console.log('=== cURL Proxy Debug ===');
                                        console.log('detail.final_proxy_used:', detail.final_proxy_used);
                                        console.log('detail.proxy_attempts:', detail.proxy_attempts);
                                        console.log('Resolved proxyUrl:', proxyUrl);
                                        console.log('result.executionConfig?.proxyParameterName:', result.executionConfig?.proxyParameterName);

                                        // Add proxy parameter if it was used
                                        if (proxyUrl) {
                                          // Find the proxy parameter name from existing query params OR use testCase config OR default
                                          let proxyParamName = Object.keys(allQueryParams).find(key =>
                                            key.toLowerCase().includes('proxy')
                                          );

                                          // If not found in query params, get from execution config > testCase > default
                                          if (!proxyParamName) {
                                            proxyParamName = result.executionConfig?.proxyParameterName ||
                                                           testCase?.proxyParameterName ||
                                                           'proxy_url';
                                          }

                                          // ALWAYS add the proxy parameter with full proxy URL (not just username)
                                          allQueryParams[proxyParamName] = proxyUrl;
                                        }

                                        // Add all query params to URL (manually to avoid encoding proxy URL)
                                        if (Object.keys(allQueryParams).length > 0) {
                                          const params = Object.entries(allQueryParams)
                                            .map(([key, value]) => {
                                              // Don't encode proxy URLs - keep them readable
                                              if (key.toLowerCase().includes('proxy')) {
                                                return `${encodeURIComponent(key)}=${value}`;
                                              }
                                              // Encode other params normally
                                              return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                                            })
                                            .join('&');
                                          url += `?${params}`;
                                        }

                                        let curlParts = [`curl -X ${method}`];

                                        // Add auth from testCase
                                        const auth = testCase?.auth;

                                        if (auth?.type === 'bearer' && auth.token) {
                                          curlParts.push(`  -H "Authorization: Bearer ${auth.token}"`);
                                        } else if (auth?.type === 'basic' && auth.username) {
                                          curlParts.push(`  -u "${auth.username}:${auth.password || ''}"`);
                                        } else if (auth?.type === 'api_key' && auth.key_name && auth.key_value) {
                                          curlParts.push(`  -H "${auth.key_name}: ${auth.key_value}"`);
                                        }

                                        // Add headers from input (these should NOT include auth - auth comes from testCase)
                                        if (detail.input.headers) {
                                          Object.entries(detail.input.headers).forEach(([key, value]) => {
                                            // Skip auth headers as they come from testCase.auth above
                                            const keyLower = key.toLowerCase();
                                            if (keyLower !== 'authorization' && !keyLower.includes('api-key') && keyLower !== auth?.key_name?.toLowerCase()) {
                                              curlParts.push(`  -H "${key}: ${value}"`);
                                            }
                                          });
                                        }

                                        // Add body
                                        if (detail.input.body && Object.keys(detail.input.body).length > 0) {
                                          curlParts.push(`  -H "Content-Type: application/json"`);
                                          curlParts.push(`  -d '${JSON.stringify(detail.input.body)}'`);
                                        }

                                        // Add URL at the end
                                        curlParts.push(`  "${url}"`);

                                        const curl = curlParts.join(' \\\n');

                                        // Copy to clipboard using helper
                                        await copyToClipboard(curl, 'cURL command copied to clipboard!');
                                      } catch (err) {
                                        console.error('Failed to generate cURL:', err);
                                        alert('Failed to generate cURL command: ' + err.message);
                                      }
                                    }}
                                    className="btn btn-primary"
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', marginLeft: '0.5rem' }}
                                  >
                                    Copy as cURL
                                  </button>
                                </div>
                                <div style={{
                                  background: '#fff',
                                  padding: '1rem',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  marginBottom: '1rem'
                                }}>
                                  {detail.input.path && (
                                    <div style={{ marginBottom: '0.5rem' }}>
                                      <strong style={{ fontSize: '0.85rem' }}>Path:</strong>
                                      <code style={{
                                        marginLeft: '0.5rem',
                                        padding: '0.25rem 0.5rem',
                                        background: '#f0f0f0',
                                        borderRadius: '3px',
                                        fontSize: '0.8rem'
                                      }}>{detail.input.path}</code>
                                    </div>
                                  )}
                                  {detail.input.query_params && Object.keys(detail.input.query_params).length > 0 && (
                                    <div style={{ marginBottom: '0.5rem' }}>
                                      <strong style={{ fontSize: '0.85rem' }}>Query Parameters:</strong>
                                      <pre style={{
                                        margin: '0.25rem 0 0 0',
                                        padding: '0.5rem',
                                        background: '#f0f0f0',
                                        borderRadius: '3px',
                                        fontSize: '0.75rem',
                                        lineHeight: '1.4'
                                      }}>
                                        {JSON.stringify(detail.input.query_params, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  {detail.input.headers && Object.keys(detail.input.headers).length > 0 && (
                                    <div style={{ marginBottom: '0.5rem' }}>
                                      <strong style={{ fontSize: '0.85rem' }}>Headers:</strong>
                                      <pre style={{
                                        margin: '0.25rem 0 0 0',
                                        padding: '0.5rem',
                                        background: '#f0f0f0',
                                        borderRadius: '3px',
                                        fontSize: '0.75rem',
                                        lineHeight: '1.4'
                                      }}>
                                        {JSON.stringify(detail.input.headers, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  {detail.input.body && (
                                    <div>
                                      <strong style={{ fontSize: '0.85rem' }}>Body:</strong>
                                      <pre style={{
                                        margin: '0.25rem 0 0 0',
                                        padding: '0.5rem',
                                        background: '#f0f0f0',
                                        borderRadius: '3px',
                                        fontSize: '0.75rem',
                                        lineHeight: '1.4'
                                      }}>
                                        {JSON.stringify(detail.input.body, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}

                            {/* Response Body Section */}
                            {detail.response_body && (
                              <>
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <strong style={{ fontSize: '1rem' }}>Response Body:</strong>
                                  <button
                                    onClick={() => copyToClipboard(JSON.stringify(detail.response_body, null, 2), 'Response copied to clipboard!')}
                                    className="btn btn-secondary"
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', marginLeft: '1rem' }}
                                  >
                                    Copy JSON
                                  </button>
                                </div>
                                <pre style={{
                                  background: '#fff',
                                  padding: '1rem',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  maxHeight: '400px',
                                  overflowY: 'auto',
                                  fontSize: '0.75rem',
                                  lineHeight: '1.4'
                                }}>
                                  {JSON.stringify(detail.response_body, null, 2)}
                                </pre>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {result.s3ResultsUrl && pagination && pagination.totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '4px'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    Page <strong>{pagination.page}</strong> of <strong>{pagination.totalPages}</strong>
                    ({pagination.total} results)
                  </div>

                  <div className="btn-group">
                    <button
                      onClick={() => fetchS3Results(1)}
                      disabled={pagination.page === 1 || loadingS3Results}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      « First
                    </button>
                    <button
                      onClick={() => fetchS3Results(currentPage - 1)}
                      disabled={pagination.page === 1 || loadingS3Results}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      ‹ Previous
                    </button>
                    <span
                      className="btn"
                      style={{
                        cursor: 'default',
                        fontSize: '0.875rem',
                        padding: '0.5rem 1rem',
                        background: '#fff'
                      }}
                    >
                      {pagination.page} / {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => fetchS3Results(currentPage + 1)}
                      disabled={!pagination.hasMore || loadingS3Results}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      Next ›
                    </button>
                    <button
                      onClick={() => fetchS3Results(pagination.totalPages)}
                      disabled={pagination.page === pagination.totalPages || loadingS3Results}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      Last »
                    </button>
                  </div>

                  <div>
                    <label style={{ marginRight: '0.5rem', fontSize: '0.9rem' }}>Items per page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(parseInt(e.target.value));
                        fetchS3Results(1); // Reset to page 1 when changing page size
                      }}
                      disabled={loadingS3Results}
                      style={{ padding: '0.375rem 0.75rem' }}
                    >
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                    </select>
                  </div>
                </div>
              )}
              </>
            ) : null}
          </div>
        )}

        <div className="mt-3">
          <p><strong>Executed:</strong> {new Date(result.createdAt).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

export default ResultDetail;
